<template>
    <div>
        <v-row class="project-cards__row row">
            <v-col class="col-lg-12">
              <ProjectCard
                title="Lucy Macroscope"
                :subtitle="macroscopeSubtitle">
                <template v-slot:tooltip>
                  <p>
                      Gain perspective by observing how communities compare to each others and globally.
                      The size of community refers to its number of members.
                  </p>
                  <p>
                    <ul>
                      <li>The <strong>All view</strong> shows communities in comparison to each other and the panel, respecting proportions as much as possible.</li>
                      <li>The <strong>Zoom view</strong> keeps the proportions but zoom-in on the selected communities. It is our recommended choice for communities of equivalent size.</li>
                      <li>The <strong>Schematic view</strong> gets rid of the proportions to provide a standardized visualization.</li>
                    </ul>
                  </p>
                  <p>Hover on the community of interest on the diagram to reveal numerical values</p>
                </template>
                <v-row>
                  <v-col class="col-6">
                    <v-radio-group class="venn-selector" v-model="vennType" :mandatory="true">
                      <v-radio label="All" value="all"></v-radio>
                      <v-radio label="Zoom" value="zoom"></v-radio>
                      <v-radio label="Schematic" value="schematic"></v-radio>
                    </v-radio-group>
                    <v-sheet class="pa-2 macroscope-info" v-if="macroscope" elevation="3">
                      Community <span class="community-name">{{ macroscope.element | join(" AND ") }}</span> &mdash;
                      <span class="community-size">{{ macroscope.size | round }}%</span> of panel.
                      <div v-if="macroscope.compl" class="ml-0">
                        Without <span class="community-name">{{ macroscope.compl.without | join(" OR ") }}</span> &mdash;
                        <span class="community-size">{{ macroscope.compl.size | round }}%</span> of panel.
                      </div>
                    </v-sheet>
                  </v-col>
                  <v-col class="col-6">
                    <div class="d-flex justify-center align-center">
                      <div id="venn"></div>
                    </div>
                  </v-col>
                </v-row>
              </ProjectCard>
            </v-col>
        </v-row>
        <v-row class="project-cards__row row">
            <v-col class="col-lg-6">
                <ProjectCard title="Activity in members"
                             :loading="lineLoading">
                    <Plotly :data="toPlotifyData(usersShareData)" :layout="chartLayout('', 'Share of users')" />
                    <template v-slot:tooltip>
                        Percentage of panel members
                        that published on a particular day and belong to the community.
                    </template>
                </ProjectCard>
            </v-col>
            <v-col class="col-lg-6">
                <ProjectCard title="Activity in messages"
                             :loading="lineLoading">
                    <Plotly :data="toPlotifyData(tweetsShareData)" :layout="chartLayout('', 'Share of messages')" />
                    <template v-slot:tooltip>
                        Percentage of messages (relative to tweets from the whole panel) published from the community
                        that on a particular day.
                    </template>
                </ProjectCard>
            </v-col>
        </v-row>
    </div>
</template>

<script>
    import ProjectCard from "./../ProjectCard";
    import axios from "axios";
    import CommunityService from "../../../services/community.service";
    import {Plotly} from 'vue-plotly'
    import * as venn from "venn.js"
    import * as d3 from "d3"
    import * as _ from "lodash"

    function round(x) {
      let p = 100 * x
      if (p === undefined || p === null) {
        return "NaN"
      } else if (p <= 0.05) {
        return "<0.1%"
      } else {
        return `${_.round(p, 1)}%`
      }
    }

    function isSubset(a, b) {
      return _.every(_.map(a, e => _.includes(b, e)))
    }

    export default {
        name: "Overview",
        props: ['project'],
        components: {
            ProjectCard,
            Plotly
        },
        data: () => ({
            CommunityService,
            selectedCommunities: [],
            lineCharts: null,
            lineLoading: false,
            newDataIteration: 0,
            vennCancelToken: null,
            cancelTokens: {
                venn: null,
                timeline: null
            },
            sources: {
                venn: null,
                timeline: null
            },
            vennLoading: false,
            vennDataset: [],
            vennType: "all",
            hover: [],
            errorMessage: null
        }),
        computed: {
          macroscope () {
            let sets = _.filter(this.hover, s => s !== 'Panel'),
              entry = _.find(this.vennSets, s => JSON.stringify(s.sets) === JSON.stringify(sets)),
              size = entry ? entry.perc : 0,
              vennDataset = this.vennDataset,
              vennSets = this.vennSets

            function complement(e) {
              let others = _.sortBy(
                _.filter(vennSets, s => isSubset(e.sets, s.sets) && s.sets.length > e.sets.length && !_.includes(s.sets, 'Panel')),
                o => o.length
              )

              let without = _.difference(_.uniq(_.flatten(_.map(others, o => o.sets))), e.sets)

              if (others.length === 0) {
                return null
              } else if (others.length === 1) {
                return { without: without, size: e.perc - others[0].perc }
              } else if (others.length === 3) {
                return { without: without, size: e.perc - (others[0].perc + others[1].perc - others[2].perc) }
              } else {
                console.error("Incorrect size of 'others' in set complement: ", others)
              }
            }

            if (entry) {
              return {element: entry.sets, size: size, compl: complement(entry)}
            } else {
              return null
            }
          },
            usersShareData () {
              return this.lineCharts ? this.lineCharts.usersSharePlot: null
            },
            tweetsShareData () {
              return this.lineCharts ? this.lineCharts.tweetsSharePlot: null
            },
          vennTrueSizes () {
            if (this.vennDataset.length === 0) {
              return []
            } else {
              return this.vennDataset.concat(this.vennDataset).concat([1])
            }
          },
          vennSizes () {
            if (this.vennType === "all") {
              return this.vennTrueSizes
            } else if (this.vennType === "zoom") {
              let max = _.max(this.vennDataset)
              return _.map(this.vennDataset, x => x / max)
            } else if (this.vennType === "schematic") {
              if (this.vennDataset.length === 7) {
                return [1, 1, 1, 0.3, 0.3, 0.3, 0.1]
              } else if (this.vennDataset.length === 3) {
                return [1, 1, 0.3]
              } else if (this.vennDataset.length === 1) {
                return [1]
              } else {
                return []
              }
            } else {
              return []
            }
          },
          macroscopeSubtitle () {
            if (this.vennType === "all") {
              return "% of French Lucy Panel"
            } else if (this.vennType === "zoom") {
              let labels = _.map(this.project.configuration.communities, c => c.label),
                  largest = _.head(_.maxBy(_.zip(labels, this.vennDataset), e => e[1]))
              return `Relative size with respect to ${largest ? largest:'largest'} community`
            } else if (this.vennType === "schematic") {
              return `Schematic view of communities`
            }
          },
          vennSets () {
            var sizes = this.vennSizes
            var sets

            if (this.vennDataset.length === 7) {
              let a = this.project.configuration.communities[0].label,
                  b = this.project.configuration.communities[1].label,
                  c = this.project.configuration.communities[2].label,
                  p = "Panel"
              sets = [ [a], [b], [c], [a, b], [a, c], [b, c], [a, b, c],
                       [p, a], [p, b], [p, c], [p, a, b], [p, a, c], [p, b, c], [p, a, b, c], [p] ]
            } else if (this.vennDataset.length === 3) {
              let a = this.project.configuration.communities[0].label,
                  b = this.project.configuration.communities[1].label,
                  p = "Panel"
              sets = [ [a], [b], [a, b], [p, a], [p, b], [p, a, b], [p] ]
            } else if (this.vennDataset.length === 1) {
              let a = this.project.configuration.communities[0].label,
                  p = "Panel"
              sets = [ [a] , [p, a], [p] ]
            } else {
              sets = []
            }
            return _.map(_.zip(sizes,
              sets.slice(0, sizes.length),
              this.vennTrueSizes.slice(0, sizes.length)),
              e => ({ sets: e[1], size: e[0], perc: e[2] }))
          }
        },
        methods: {
            async getLineChartsData() {
                this.handleCancelRequest('timeline');
                this.lineLoading = true;
                this.errorMessage = null;
                this.newDataIteration += 1;
                this.CommunityService.getTimelines({
                        startDate: this.project.timeframe_start,
                        endDate: this.project.timeframe_end,
                        queries: this.project.configuration.communities.map(com => com.query)
                    },
                    this.sources.timeline
                ).then(resp => {
                    if (!resp.message) {
                        this.lineLoading = false;
                        this.lineCharts = resp;
                        this.newDataIteration += 1
                    } else if (resp.message !== "Operation canceled by the user.") {
                        this.lineLoading = false;
                        this.errorMessage = resp.message
                    }
                });
                this.lineCharts = [];
            },
            async getVennDiagramData() {
                this.handleCancelRequest('venn');
                this.vennLoading = true;
                this.errorMessage = null;
                this.CommunityService.getVennData({
                        startDate: this.project.timeframe_start,
                        endDate: this.project.timeframe_end,
                        queries: this.project.configuration.communities.map(com => com.query)
                    },
                    this.sources.venn
                ).then(resp => {
                    if (!resp.message) {
                        this.vennLoading = false;
                        this.vennDataset = resp;
                    } else if (resp.message !== "Operation canceled by the user.") {
                        this.vennLoading = false;
                        this.errorMessage = resp.message
                    }
                });
                this.vennDataset = [];
            },
            handleCancelRequest(type) {
                if (this.sources[type]) {
                    this.sources[type].cancel('Operation canceled by the user.');
                }
                this.cancelTokens[type] = axios.CancelToken;
                this.sources[type] = this.cancelTokens[type].source();
            },
            toPlotifyData(d, title) {

                if (d === undefined) {
                    return [];
                }

                let communities = _.map(this.project.configuration.communities, c => c.label);
                let tracesWithLabels = _.zip(communities, d);

                return _.map(tracesWithLabels, (a) => {
                    return {
                        x: _.map(a[1], e => e.x),
                        y: _.map(a[1], e => e.y),
                        type: "scatter",
                        mode: "lines",
                        name: a[0]
                    };
                });
            },
            chartLayout(title, ylabel) {
                return {
                    yaxis: {
                        title: ylabel,
                        range: [0, 100],
                        color: '#8798ad'
                    },
                    xaxis: {
                        color: '#8798ad'
                    },
                    showLegend: false
                };
            },
          drawVenn () {
            let component = this
            if (this.vennSets.length === 0) {
              return
            }
            var chart = venn.VennDiagram()
              .width(300)
              .height(300);

            var div = d3.select("#venn")
            div.datum(this.vennSets).call(chart);

            div.selectAll("path")
              .style("stroke-opacity", 0)
              .style("stroke", "#fff")
              .style("stroke-width", 3)

            div.selectAll("g")
              .on("mouseover", function(d, i) {
                component.hover = d.sets

                // sort all the areas relative to the current item
                venn.sortAreas(div, d);

                // highlight the current path
                var selection = d3.select(this)
                selection.select("path").transition().duration(200)
                  .style("fill-opacity", d.sets.length == 1 ? .4 : .1)
                  .style("stroke-opacity", 1);
              })
              .on("mouseout", function(d, i) {
                component.hover = []
                var selection = d3.select(this)
                selection.select("path").transition().duration(200)
                  .style("fill-opacity", d.sets.length == 1 ? .25 : .0)
                  .style("stroke-opacity", 0);
              });
          }
        },
      filters: {
        join: function (value, op) {
          if (value) {
            return value.join(op)
          } else {
            return ""
          }
        },
        round: x => _.round(100*x, 2)//(100*x).toPrecision(2)
      },
        mounted() {
            this.CommunityService = new CommunityService();
            if (this.project.configuration.communities.length){
              this.getLineChartsData();
              this.getVennDiagramData();
            }
        },
        watch: {
            project: {
                handler: function (val) {
                    if (val && val.configuration.communities.length) {
                        this.getLineChartsData();
                        this.getVennDiagramData();
                    }
                },
                deep: true
            },
          vennSets: {
            handler (val) {
              this.drawVenn()
            },
            depp: true
          }
        }
    }
</script>

<style>
.venn-selector .v-label {
  font-size: 12px;
}
</style>
